const SEARCH_IMAGE_SELECTOR = [
  '#alpha-header-home-search .ahhs__product-image img',
  '#alpha-header-home-search .ahhs__thumb img',
  '.ahsi__product-image img',
  '.ahsi__thumb img',
].join(',');

const resolutionCache = new Map();

const normalize = (value) => String(value || '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/\s+/g, ' ')
  .trim();

const christianSearchQuery = (query) => {
  const cleanQuery = String(query || '')
    .replace(/["()\\]/g, ' ')
    .replace(/\b(?:AND|OR|NOT)\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return cleanQuery
    ? `(${cleanQuery}) AND product_type:"Christian art"`
    : 'product_type:"Christian art"';
};

const resourceImage = (resource) => {
  const image = resource?.featured_image || resource?.image || '';
  return typeof image === 'string' ? image : image?.url || image?.src || '';
};

const sizedImage = (source) => {
  if (!source) return '';
  try {
    const url = new URL(source, window.location.origin);
    url.searchParams.set('width', '520');
    return url.toString();
  } catch (_error) {
    return source;
  }
};

const markMissing = (image) => {
  image.parentElement?.classList.add('is-missing');
  image.remove();
};

const findCurrentResource = async (type, title) => {
  const cacheKey = `${type}:${normalize(title)}`;
  if (resolutionCache.has(cacheKey)) return resolutionCache.get(cacheKey);

  const request = (async () => {
    if (type === 'product') {
      const customParams = new URLSearchParams({
        q: christianSearchQuery(title),
        type: 'product',
        view: 'alpha-home-products',
      });
      const customResponse = await fetch(`/search?${customParams.toString()}`, {
        headers: { Accept: 'application/json' },
      }).catch(() => null);
      if (customResponse?.ok) {
        const customPayload = await customResponse.json().catch(() => null);
        const normalizedTitle = normalize(title);
        const customProducts = customPayload?.products || [];
        const enrichedProduct = customProducts.find((item) => normalize(item?.title) === normalizedTitle)
          || customProducts.find((item) => {
            const candidate = normalize(item?.title);
            return candidate.includes(normalizedTitle) || normalizedTitle.includes(candidate);
          });
        if (resourceImage(enrichedProduct)) return enrichedProduct;
      }
    }

    const params = new URLSearchParams({
      q: type === 'product' ? christianSearchQuery(title) : title,
      'resources[type]': type,
      'resources[limit]': '6',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
    });
    const response = await fetch(`/search/suggest.json?${params.toString()}`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;

    const payload = await response.json();
    const resources = payload?.resources?.results?.[`${type}s`] || [];
    const normalizedTitle = normalize(title);
    return resources.find((item) => normalize(item?.title || item?.name) === normalizedTitle)
      || resources.find((item) => {
        const candidate = normalize(item?.title || item?.name);
        return candidate.includes(normalizedTitle) || normalizedTitle.includes(candidate);
      })
      || resources[0]
      || null;
  })().catch(() => null);

  resolutionCache.set(cacheKey, request);
  return request;
};

const repairImage = async (image) => {
  if (image.dataset.alphaImageRepairAttempted === 'true') return;
  image.dataset.alphaImageRepairAttempted = 'true';
  image.onerror = null;

  const link = image.closest('a[href]');
  const route = link?.getAttribute('href') || '';
  const type = route.includes('/collections/') ? 'collection' : 'product';
  const title = link?.querySelector(
    '.ahhs__product-title, .ahhs__card-title, .ahsi__product-title, .ahsi__card-title',
  )?.textContent?.trim() || image.alt || '';

  if (!title) {
    markMissing(image);
    return;
  }

  const resource = await findCurrentResource(type, title);
  const source = sizedImage(resourceImage(resource));
  if (!source) {
    markMissing(image);
    return;
  }

  if (resource?.url && link) link.href = resource.url;
  image.parentElement?.classList.remove('is-missing');
  image.addEventListener('error', () => markMissing(image), { once: true });
  image.src = source;
};

document.addEventListener('error', (event) => {
  const image = event.target;
  if (!(image instanceof HTMLImageElement) || !image.matches(SEARCH_IMAGE_SELECTOR)) return;
  if (image.dataset.alphaImageRepairAttempted === 'true') return;
  event.stopImmediatePropagation();
  repairImage(image);
}, true);

const MISSING_IMAGE_SELECTOR = [
  '#alpha-header-home-search .ahhs__product-image.is-missing',
  '.ahsi__product-image.is-missing',
].join(',');

const repairMissingContainer = async (container) => {
  if (!(container instanceof HTMLElement)) return;
  if (container.dataset.alphaImageRepairAttempted === 'true') return;
  container.dataset.alphaImageRepairAttempted = 'true';

  const link = container.closest('a[href]');
  const title = link?.querySelector('.ahhs__product-title, .ahsi__product-title')?.textContent?.trim() || '';
  if (!title) return;

  const resource = await findCurrentResource('product', title);
  const source = sizedImage(resourceImage(resource));
  if (!source) return;

  const image = document.createElement('img');
  image.src = source;
  image.alt = title;
  image.loading = 'lazy';
  image.decoding = 'async';
  image.width = 520;
  image.height = 650;
  image.addEventListener('error', () => markMissing(image), { once: true });
  if (resource?.url && link) link.href = resource.url;
  container.classList.remove('is-missing');
  container.replaceChildren(image);
};

const repairMissingCards = (root = document) => {
  if (root instanceof Element && root.matches(MISSING_IMAGE_SELECTOR)) {
    repairMissingContainer(root);
  }
  root.querySelectorAll?.(MISSING_IMAGE_SELECTOR).forEach(repairMissingContainer);
};

const startMissingCardObserver = () => {
  repairMissingCards(document);
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) repairMissingCards(node);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startMissingCardObserver, { once: true });
} else {
  startMissingCardObserver();
}
